import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getPending: () => api.get('/users/pending'),
  create: (data) => api.post('/users', data),
  approve: (userId) => api.post(`/users/${userId}/approve`),
  reject: (userId) => api.post(`/users/${userId}/reject`),
  updateRole: (userId, role) => api.put(`/users/${userId}/role?role=${role}`),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  getSalesReps: () => api.get('/users/sales-reps'),
  getImportTemplate: () => api.get('/users/import/template', { responseType: 'blob' }),
  importUsers: (csvContent) => api.post('/users/import', null, { params: { file_content: csvContent } }),
};

// Gmail API
export const gmailAPI = {
  getStatus: () => api.get('/oauth/gmail/status'),
  connect: () => api.get('/oauth/gmail/connect'),
  disconnect: () => api.delete('/oauth/gmail/disconnect'),
  getEmails: (params) => api.get('/gmail/emails', { params }),
  getEmail: (messageId) => api.get(`/gmail/email/${messageId}`),
  sendEmail: (data) => api.post('/gmail/send', data),
};

// Leads API
export const leadsAPI = {
  create: (data) => api.post('/leads', data),
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  import: (csvContent) => api.post('/leads/import', null, { params: { file_content: csvContent } }),
  getImportTemplate: () => api.get('/leads/import/template', { responseType: 'blob' }),
  markPaid: (id, amount) => api.post(`/leads/${id}/mark-paid?amount=${amount}`),
  // File uploads
  getFiles: (leadId) => api.get(`/leads/${leadId}/files`),
  uploadFile: (leadId, fileType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/leads/${leadId}/files/upload?file_type=${fileType}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadFile: (leadId, fileId) => api.get(`/leads/${leadId}/files/${fileId}/download`, { responseType: 'blob' }),
  deleteFile: (leadId, fileId) => api.delete(`/leads/${leadId}/files/${fileId}`),
};

// Payouts API
export const payoutsAPI = {
  getAll: (params) => api.get('/payouts', { params }),
  updateStatus: (id, status) => api.put(`/payouts/${id}/status?status=${status}`),
};

// Nonprofits API
export const nonprofitsAPI = {
  create: (data) => api.post('/nonprofits', data),
  getAll: () => api.get('/nonprofits'),
};

// Vendors API
export const vendorsAPI = {
  create: (data) => api.post('/vendors', data),
  getAll: (activeOnly = false) => api.get('/vendors', { params: { active_only: activeOnly } }),
  getById: (id) => api.get(`/vendors/${id}`),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Commission Structure API
export const commissionAPI = {
  getStructure: () => api.get('/commission-structure'),
  updateStructure: (data) => api.put('/commission-structure', data),
  getMatrix: () => api.get('/commission-matrix'),
  getRepTiers: () => api.get('/rep-tiers'),
};

// Reports API
export const reportsAPI = {
  getDashboardStats: () => api.get('/dashboard/stats'),
  getSalesRepReport: () => api.get('/reports/sales-reps'),
  exportCSV: () => api.get('/reports/export/csv', { responseType: 'blob' }),
  getYTDCommission: (repId, year) => api.get(`/reports/ytd-commission/${repId}`, { params: { year } }),
  getAllYTDCommission: (year) => api.get('/reports/ytd-commission', { params: { year } }),
  // PDF exports
  exportRepPDF: (repId, startDate, endDate) => api.get(`/reports/pdf/rep/${repId}`, {
    responseType: 'blob',
    params: { start_date: startDate, end_date: endDate }
  }),
  exportCompanyPDF: (startDate, endDate) => api.get('/reports/pdf/company', {
    responseType: 'blob',
    params: { start_date: startDate, end_date: endDate }
  }),
};

// Reference data API
export const refDataAPI = {
  getMilestones: () => api.get('/milestones'),
  getLeadTypes: () => api.get('/lead-types'),
};

export default api;
